import React, { useState } from "react";
import financeV2Adapters from "../../../../adapters/financeV2Adapters";
import Loader from "../../../common/Loader";
import tools from "../../../../helpers/tools";
import EnveloppeConfigPanel from "./EnveloppeConfigPanel";
import styles from "./EnveloppeTable.module.scss";

const EMPTY_FORM = {
  montant: "",
  nom: "",
  typeFinancement: "",
  dateMaximale: "",
};

const TYPE_OPTIONS = Object.entries(financeV2Adapters.FINANCE_TYPE_MAP);

const fmt = (n) => Number(n || 0).toLocaleString("fr-FR");

/**
 * Tableau éditable des enveloppes d'un financeur (composant contrôlé).
 * La donnée et les opérations viennent du parent (via `useEnveloppes`).
 *
 * Props :
 * - enveloppes : liste (actives + clôturées), chacune avec sa consommation.
 * - isLoading  : chargement initial.
 * - disabled   : désactive création/édition/clôture.
 * - onCreate(form) / onUpdate(uuid, form) / onClose(uuid) : Promise<bool>.
 * - onModifierEtapes(uuid, etapesActives) / onSynchroniserTodos(uuid, todos) : Promise<bool>.
 */
const EnveloppeTable = ({
  enveloppes = [],
  isLoading = false,
  disabled = false,
  onCreate,
  onUpdate,
  onClose,
  onModifierEtapes,
  onSynchroniserTodos,
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingUuid, setEditingUuid] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [configUuid, setConfigUuid] = useState(null);

  const actives = enveloppes.filter((e) => e.estActive);
  const closed = enveloppes.filter((e) => !e.estActive);

  const change = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingUuid(null);
    setShowForm(false);
  };

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditingUuid(null);
    setShowForm(true);
  };

  const openEdit = (env) => {
    setForm({
      montant: env.montant,
      nom: env.nom,
      typeFinancement: env.typeFinancement,
      dateMaximale: env.dateMaximale ? env.dateMaximale.slice(0, 10) : "",
    });
    setEditingUuid(env.uuid);
    setShowForm(true);
  };

  const montantValide = form.montant !== "" && Number(form.montant) > 0;

  const submit = async () => {
    if (!montantValide || submitting) return;
    setSubmitting(true);
    const ok = editingUuid
      ? await onUpdate(editingUuid, form)
      : await onCreate(form);
    setSubmitting(false);
    if (ok) resetForm();
  };

  const renderRow = (env) => {
    const dispo = env.consommation.disponible;
    const origine = env.consommation.enveloppeOrigine || env.montant;
    const ratio = financeV2Adapters.enveloppeRatio(
      env.consommation.reserve + env.consommation.engage,
      origine,
    );
    const dispoColor = env.estActive
      ? financeV2Adapters.enveloppeColorText(ratio)
      : "#adb5bd";
    const configOpen = configUuid === env.uuid;
    return (
      <React.Fragment key={env.uuid}>
      <tr className={env.estActive ? "" : styles.rowClosed}>
        <td>{env.nom || "—"}</td>
        <td>
          {env.typeFinancement ? (
            <span className={styles.typeBadge}>
              {financeV2Adapters.FINANCE_TYPE_MAP[env.typeFinancement] ||
                env.typeFinancement}
            </span>
          ) : (
            <span className={styles.typeNone}>non défini</span>
          )}
        </td>
        <td>
          {env.dateMaximale ? tools.formatDate(env.dateMaximale) : "—"}
        </td>
        <td className={styles.num}>{fmt(env.montant)} €</td>
        <td className={styles.dispo} style={{ color: dispoColor }}>
          {fmt(dispo)} €
        </td>
        <td className={styles.actions}>
          {env.estActive && (
            <button
              type="button"
              className={styles.btnIcon}
              onClick={() => setConfigUuid(configOpen ? null : env.uuid)}
              title="Configurer les étapes et todos"
              aria-label={`Configurer l'enveloppe ${env.nom || ""}`}
              aria-expanded={configOpen}
            >
              <i className="fa fa-tasks" />
            </button>
          )}
          {env.estActive && !disabled && (
            <>
              <button
                type="button"
                className={styles.btnIcon}
                onClick={() => openEdit(env)}
                title="Modifier"
                aria-label={`Modifier l'enveloppe ${env.nom || ""}`}
              >
                <i className="fa fa-pen" />
              </button>
              <button
                type="button"
                className={styles.btnIconDanger}
                onClick={() => onClose(env.uuid)}
                title="Clôturer"
                aria-label={`Clôturer l'enveloppe ${env.nom || ""}`}
              >
                <i className="fa fa-lock" />
              </button>
            </>
          )}
        </td>
      </tr>
      {configOpen && (
        <tr>
          <td colSpan={6} style={{ padding: 0 }}>
            <EnveloppeConfigPanel
              enveloppe={env}
              onModifierEtapes={onModifierEtapes}
              onSynchroniserTodos={onSynchroniserTodos}
            />
          </td>
        </tr>
      )}
      </React.Fragment>
    );
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.head}>
        <h3 className={styles.title}>Enveloppes</h3>
        {!showForm && !disabled && (
          <button type="button" className={styles.btnAdd} onClick={openCreate}>
            <i className="fa fa-plus" /> Nouvelle enveloppe
          </button>
        )}
      </div>

      {showForm && (
        <div className={styles.form}>
          <div className={styles.formGrid}>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Montant (€) *</span>
              <input
                type="number"
                name="montant"
                value={form.montant}
                onChange={change}
                min="0"
                step="0.01"
                className={styles.input}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Nom</span>
              <input
                type="text"
                name="nom"
                value={form.nom}
                onChange={change}
                placeholder="ex. BPCE Lease 2026"
                className={styles.input}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Type de financement</span>
              <select
                name="typeFinancement"
                value={form.typeFinancement}
                onChange={change}
                className={styles.input}
              >
                <option value="">— Sélectionner —</option>
                {TYPE_OPTIONS.map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Date maximale</span>
              <input
                type="date"
                name="dateMaximale"
                value={form.dateMaximale}
                onChange={change}
                className={styles.input}
              />
            </label>
          </div>
          {!form.typeFinancement && (
            <div className={styles.hint}>
              <i className="fa fa-info-circle" /> Sans type de financement, cette
              enveloppe ne pourra pas être utilisée dans une demande.
            </div>
          )}
          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.btnSave}
              onClick={submit}
              disabled={!montantValide || submitting}
            >
              <i className="fa fa-save" />{" "}
              {editingUuid ? "Mettre à jour" : "Créer"}
            </button>
            <button
              type="button"
              className={styles.btnCancel}
              onClick={resetForm}
              disabled={submitting}
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {isLoading && enveloppes.length === 0 ? (
        <Loader />
      ) : enveloppes.length === 0 ? (
        <div className={styles.empty}>Aucune enveloppe pour ce financeur.</div>
      ) : (
        <div className={styles.tableScroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Nom</th>
                <th>Type</th>
                <th>Date maximale</th>
                <th className={styles.num}>Montant</th>
                <th className={styles.num}>Disponible</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {actives.map(renderRow)}
              {showClosed && closed.map(renderRow)}
            </tbody>
          </table>
          {closed.length > 0 && (
            <button
              type="button"
              className={styles.closedToggle}
              onClick={() => setShowClosed((v) => !v)}
              aria-expanded={showClosed}
            >
              <i className={`fa fa-chevron-${showClosed ? "up" : "down"}`} />
              {showClosed
                ? "Masquer les enveloppes clôturées"
                : `Afficher les enveloppes clôturées (${closed.length})`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default EnveloppeTable;
